import * as React from 'react';
import {
  Alert, Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, InputAdornment, Snackbar, Stack, Switch, TextField,
  ThemeProvider, Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerIcon from '@mui/icons-material/Timer';
import StorageIcon from '@mui/icons-material/Storage';
import ScienceIcon from '@mui/icons-material/Science';
import { grey } from '@mui/material/colors';

import theme from '../../../themeContent';
import api from '../../../services/api';
import { useAuth } from '../../../components/AuthProvider';

const EMPTY = { min_size_backup: '', timeout: '', time_backup: '', demo_mode: 0 };

export default function FormDialog(props) {
  const auth = useAuth();
  const [state, setState] = React.useState(EMPTY);
  const [snack, setSnack] = React.useState({ open: false, msg: '', sev: 'success' });
  const canEdit = auth.hasPerm('config.update');

  const handleChange = (e) => setState((s) => ({ ...s, [e.target.name]: e.target.value }));

  React.useEffect(() => {
    if (!props.open) return;
    setState(EMPTY);
    api.get('hermes/config/')
      .then((res) => setState(res.data))
      .catch(() => setState(EMPTY));
  }, [props.open]);

  const handleSubmit = (event) => {
    event.preventDefault();
    api.put('hermes/config/', state)
      .then(() => {
        setSnack({ open: true, msg: 'Configurações salvas com sucesso.', sev: 'success' });
        props.handlerClose();
      })
      .catch(() => setSnack({ open: true, msg: 'Não foi possível salvar.', sev: 'error' }));
  };

  const seedDemo = () => {
    api.post('hermes/demo/seed')
      .then((r) => {
        setState((s) => ({ ...s, demo_mode: 1 }));
        setSnack({ open: true, msg: r.data?.message || 'Demo populado.', sev: 'success' });
      })
      .catch(() => setSnack({ open: true, msg: 'Falha ao popular demo.', sev: 'error' }));
  };

  const clearDemo = () => {
    api.post('hermes/demo/clear')
      .then((r) => {
        setState((s) => ({ ...s, demo_mode: 0 }));
        setSnack({ open: true, msg: r.data?.message || 'Demo removido.', sev: 'success' });
      })
      .catch(() => setSnack({ open: true, msg: 'Falha ao limpar demo.', sev: 'error' }));
  };

  return (
    <ThemeProvider theme={theme}>
      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>

      <Dialog open={props.open} onClose={props.handlerClose} fullWidth maxWidth="sm"
              PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Cabeçalho */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2.5,
                     bgcolor: 'primary.main', color: '#fff' }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,.18)', color: '#fff' }}>
              <TuneIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                Configurações de Backup
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Agendamento e regras aplicadas a todos os hosts
              </Typography>
            </Box>
          </Box>

          <DialogContent sx={{ bgcolor: grey[50], pt: 3 }}>
            <Stack spacing={2.5}>
              <TextField
                disabled={!canEdit}
                required
                size="small"
                name="time_backup"
                label="Horário do backup diário"
                type="time"
                fullWidth
                value={state?.time_backup || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><ScheduleIcon color="action" /></InputAdornment>
                ) }}
                helperText="Hora em que o ciclo automático de backup é disparado."
              />

              <TextField
                disabled={!canEdit}
                required
                size="small"
                name="timeout"
                label="Tempo máximo por backup (segundos)"
                type="number"
                fullWidth
                value={state?.timeout || ''}
                onChange={handleChange}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><TimerIcon color="action" /></InputAdornment>
                ) }}
                helperText="Após esse tempo a coleta de um host é abortada."
              />

              <TextField
                disabled={!canEdit}
                required
                size="small"
                name="min_size_backup"
                label="Tamanho mínimo do backup (bytes)"
                type="number"
                fullWidth
                value={state?.min_size_backup || ''}
                onChange={handleChange}
                InputProps={{ startAdornment: (
                  <InputAdornment position="start"><StorageIcon color="action" /></InputAdornment>
                ) }}
                helperText="Coletas menores que isso são descartadas (proteção contra captura incompleta)."
              />

              <Divider textAlign="left">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  <ScienceIcon fontSize="small" /> Demonstração
                </Box>
              </Divider>
              <FormControlLabel
                control={<Switch checked={!!state.demo_mode} disabled={!canEdit}
                                 onChange={(e) => setState((s) => ({ ...s, demo_mode: e.target.checked ? 1 : 0 }))} />}
                label="Modo Demonstração (backups mockados — qualquer host 'funciona')"
              />
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" disabled={!canEdit} onClick={seedDemo}>
                  Popular dados demo
                </Button>
                <Button size="small" variant="outlined" color="error" disabled={!canEdit} onClick={clearDemo}>
                  Limpar dados demo
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                "Popular" cria ~10 equipamentos fake (OLT, rádios, switches, BGP) e liga o modo demo
                — o mapa de Inventário e os backups passam a funcionar sem rede real.
              </Typography>
            </Stack>
          </DialogContent>

          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button variant="outlined" onClick={props.handlerClose}>Fechar</Button>
            <Button variant="contained" color="primary" type="submit" disabled={!canEdit}>
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </ThemeProvider>
  );
}
